import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref, watch } from 'vue';
import { Route } from 'vue-router';
import YouTubePlayer from 'youtube-player';
import { YoutubePlayerOptions } from '@/interfaces/components/youtube-player';
import { PlayerIcon } from '@/enums/player-icon';
import VideoPlayerWrapper from './VideoPlayerWrapper.vue';
import { TrailerClip } from '@/interfaces/trailerClip';
import { Clip } from '@/interfaces/titles';

type YouTubePlayerType = ReturnType<typeof YouTubePlayer> & {
	off: (event: any) => void;
};

enum YoutubePlayerStatus {
	UNSTARTED = '-1',
	ENDED = 0,
	PLAYING = 1,
	PAUSED = 2,
	BUFFERING = 3,
	CUED = 5,
}

enum YoutubePlayerEvent {
	READY = 'ready',
	STATE_CHANGE = 'stateChange',
}


export default /*#__PURE__*/_defineComponent({
  __name: 'YoutubePlayer',
  props: {
    clip: null,
    options: null,
    source: { default: '' },
    width: { default: '100%' },
    height: null,
    isActive: { type: Boolean },
    activeRoute: null,
    isBackdrop: { type: Boolean },
    playIconType: null,
    isDisneyPlusOffer: { type: Boolean }
  },
  emits: ["setActiveClipId", "onPlaying", "onPaused", "onEnded", "onBuffering"],
  setup(__props: any, { emit }: { emit: ({
	(e: 'setActiveClipId', clipId: string | null): void;
	(e: 'onPlaying', clip: TrailerClip | Clip): void;
	(e: 'onPaused', clip: TrailerClip | Clip): void;
	(e: 'onEnded', clip: TrailerClip | Clip): void;
	(e: 'onBuffering', clip: TrailerClip | Clip): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { clip: TrailerClip | Clip, options: YoutubePlayerOptions, source: string, width: number | string, height?: number | string, isActive?: boolean, activeRoute?: Route, isBackdrop?: boolean, playIconType?: PlayerIcon, isDisneyPlusOffer?: boolean };





const player = ref<YouTubePlayerType | null>(null);

const externalId = computed(() => {
	if ('externalId' in props.clip) {
		return props.clip.externalId;
	}
	return props.clip.external_id;
});

const playerEventListeners = ref<(YouTubePlayerType['on'] | void)[]>([]);

watch(
	() => props.isActive,
	() => {
		if (player.value && !props.isActive) {
			player.value.pauseVideo();
		}
	}
);

function destroyPlayer() {
	if (playerEventListeners.value?.length) {
		playerEventListeners.value.forEach(playerEventListener => {
			player.value?.off(playerEventListener);
		});
	}

	if (props.isActive) {
		emit('setActiveClipId', null);
	}

	if (player.value) {
		player.value.destroy();
		player.value = null;
	}
}

watch(
	() => props.activeRoute,
	() => {
		destroyPlayer();
	}
);

const previewImageUrl = computed(() =>
	props.clip ? `https://img.youtube.com/vi/${externalId.value}/mqdefault.jpg` : ''
);
const playerOptions = computed<YoutubePlayerOptions>(() => {
	return {
		autohide: 1,
		iv_load_policy: 3,
		modestbranding: 1,
		rel: 0,
		mute: 0,
		...props.options,
	};
});

const playerId = computed(() => {
	return `youtube-player-${externalId.value}-${props.source}`;
});

function onStateChange(event: { data: YoutubePlayerStatus }) {
	if (event.data === YoutubePlayerStatus.PLAYING) {
		emit('setActiveClipId', externalId.value);
		emit('onPlaying', props.clip);
	}

	if (event.data === YoutubePlayerStatus.PAUSED) {
		emit('onPaused', props.clip);
	}

	if (event.data === YoutubePlayerStatus.ENDED) {
		emit('setActiveClipId', null);
		emit('onEnded', props.clip);
	}

	if (event.data === YoutubePlayerStatus.BUFFERING) {
		emit('onBuffering', props.clip);
	}
}

function play() {
	if (player.value) {
		player.value.playVideo();
	} else {
		player.value = YouTubePlayer(playerId.value, {
			...(props.width ? { width: props.width } : {}),
			...(props.height ? { height: props.height } : {}),
			videoId: externalId.value,
			playerVars: playerOptions.value,
		}) as YouTubePlayerType;

		playerEventListeners.value.push(
			player.value.on(YoutubePlayerEvent.READY, () => {
				emit('setActiveClipId', externalId.value);
				player.value?.playVideo();
			})
		);

		playerEventListeners.value.push(
			player.value.on(YoutubePlayerEvent.STATE_CHANGE, (event: any) => {
				onStateChange(event);
			})
		);
	}
}

onMounted(() => {
	if (props.isActive) {
		setTimeout(() => {
			play();
		}, 0);
	}
});

return { __sfc: true,props, emit, player, externalId, playerEventListeners, destroyPlayer, previewImageUrl, playerOptions, playerId, onStateChange, play, YoutubePlayerStatus, YoutubePlayerEvent, VideoPlayerWrapper }
}

})